import {Navbar, NavbarProps} from '@common/ui/navigation/navbar/navbar';
import {Button} from '@common/ui/buttons/button';
import {Trans} from '@common/i18n/trans';
import {Link, useNavigate} from 'react-router-dom';
import {useSettings} from '@common/core/settings/use-settings';
import {useAccountUsage} from '@app/editor/use-account-usage';
import {useAccountPlan} from '@common/billing/use-account-plan';
import {AiIcon} from '@app/editor/ai/ai-icon';
import {DialogTrigger} from '@common/ui/overlays/dialog/dialog-trigger';
import {Dialog} from '@common/ui/overlays/dialog/dialog';
import {DialogHeader} from '@common/ui/overlays/dialog/dialog-header';
import {DialogBody} from '@common/ui/overlays/dialog/dialog-body';
import {FormattedNumber} from '@common/i18n/formatted-number';
import {FormattedDate} from '@common/i18n/formatted-date';
import {useNumberFormatter} from '@common/i18n/use-number-formatter';
import {useTrans} from '@common/i18n/use-trans';
import {IconButton} from '@common/ui/buttons/icon-button';
import {MenuOpenIcon} from '@common/icons/material/MenuOpen';
import {DashboardLayoutContext} from '@common/ui/layout/dashboard-layout-context';
import {setInLocalStorage} from '@common/utils/hooks/local-storage';
import {SubscriptionExpiryBanner} from '@common/billing/subscription-plan-ui';
import {SiteBanner} from '@app/site-banner/site-banner';
import clsx from 'clsx';
import {useContext} from 'react';

function CreditsBalanceNavItem() {
  const {trans} = useTrans();
  const {data: accountUsage} = useAccountUsage();
  const {data: plan} = useAccountPlan();
  const balance = accountUsage?.ai?.credits ?? null;
  const numberFormatter = useNumberFormatter();
  const navigate = useNavigate();

  if (balance === null) return null;

  return (
    <DialogTrigger type="popover">
      <Button
        variant="text"
        size="xs"
        color="primary"
        startIcon={<AiIcon />}
        aria-label={trans({
          message: 'Credits: :balance',
          values: {balance: numberFormatter.format(balance)},
        })}
        className="min-w-0"
      >
        <FormattedNumber value={balance} />
      </Button>
      <Dialog size="sm">
        <DialogHeader showDivider>
          <Trans message="Credits" />
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-12">
            <p className="text-base">
              <Trans message="Credits balance" />:{' '}
              <strong>
                <FormattedNumber value={balance} />
              </strong>
            </p>
            {plan?.name ? (
              <p className="text-sm text-muted">
                <Trans message="Current tariff" />:{' '}
                <strong className="text-main">{plan.name}</strong>
                {plan.ends_at ? (
                  <>
                    {' · '}
                    <Trans
                      message="Valid until :date"
                      values={{
                        date: (
                          <FormattedDate preset="long" date={plan.ends_at} />
                        ),
                      }}
                    />
                  </>
                ) : null}
              </p>
            ) : null}
            <div className="rounded-lg border border-primary/30 bg-primary/5 px-12 py-10 text-sm text-muted">
              <Trans message="Credits are spent when you use the AI assistant or generate images. Each action uses a small amount from your balance." />
            </div>
            <Button
              variant="text"
              size="sm"
              color="primary"
              className="self-start"
              elementType={Link}
              to="/account-settings/credits/transactions"
            >
              <Trans message="Credits transaction history" />
            </Button>
            <Button
              variant="flat"
              size="sm"
              color="primary"
              onClick={() => navigate('/checkout/credits')}
            >
              <Trans message="Top up" />
            </Button>
          </div>
        </DialogBody>
      </Dialog>
    </DialogTrigger>
  );
}

export interface UserCabinetNavbarProps
  extends Omit<NavbarProps, 'menuPosition' | 'rightChildren' | 'toggleButton'> {
  hideToggleButton?: boolean;
}

export function UserCabinetNavbar({
  className,
  hideToggleButton,
  size = 'md',
  ...props
}: UserCabinetNavbarProps) {
  const {billing} = useSettings();
  const layoutContext = useContext(DashboardLayoutContext);
  const isInDashboardLayout = layoutContext != null;

  const shouldToggleCompactMode =
    isInDashboardLayout &&
    layoutContext.leftSidenavCanBeCompact &&
    !layoutContext.isMobileMode;
  const shouldShowToggle =
    !hideToggleButton &&
    isInDashboardLayout &&
    (layoutContext.isMobileMode || layoutContext.leftSidenavCanBeCompact);

  const handleToggle = () => {
    if (!isInDashboardLayout) return;
    layoutContext.setLeftSidenavStatus(
      layoutContext.leftSidenavStatus === 'open' ? 'closed' : 'open',
    );
  };

  const handleCompactModeToggle = () => {
    if (!isInDashboardLayout) return;
    const newStatus =
      layoutContext.leftSidenavStatus === 'compact' ? 'open' : 'compact';
    setInLocalStorage(
      `${layoutContext.name}.sidenav.compact`,
      newStatus === 'compact',
    );
    layoutContext.setLeftSidenavStatus(newStatus);
  };

  // Banners must share the navbar grid cell — loose siblings break dashboard-grid.
  return (
    <div
      className={clsx(
        'flex flex-shrink-0 flex-col',
        isInDashboardLayout && 'dashboard-grid-navbar',
      )}
    >
      <Navbar
        menuPosition="dashboard"
        size={size}
        className={className}
        toggleButton={
          shouldShowToggle ? (
            <IconButton
              size="md"
              onClick={() => {
                if (shouldToggleCompactMode) {
                  handleCompactModeToggle();
                } else {
                  handleToggle();
                }
              }}
            >
              <MenuOpenIcon />
            </IconButton>
          ) : undefined
        }
        rightChildren={
          <>
            <CreditsBalanceNavItem />
            {billing.enable ? (
              <Button
                variant="outline"
                size="xs"
                color="primary"
                elementType={Link}
                to="/pricing"
              >
                <Trans message="Pay" />
              </Button>
            ) : null}
          </>
        }
        {...props}
      />
      <SiteBanner />
      {billing.enable ? <SubscriptionExpiryBanner /> : null}
    </div>
  );
}

/** @deprecated Use UserCabinetNavbar */
export const DashboardNavbar = UserCabinetNavbar;
